/**
 * CountryBackfillModal — shown to logged-in users whose community profile
 * has no `country` field (V1 migrants). Non-blocking (user can skip once per session).
 * Saves via PATCH /api/community/profile/:email/country. i18n-aware.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SKIP_KEY = 'country_backfill_skipped_until';

export default function CountryBackfillModal() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState(null);
  const [country, setCountry] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const e = localStorage.getItem('communityUserEmail');
    const token = localStorage.getItem('communitySessionToken');
    if (!e || !token) return;

    // Honor skip flag (24h window)
    const skippedUntil = parseInt(localStorage.getItem(SKIP_KEY) || '0', 10);
    if (Date.now() < skippedUntil) return;

    setEmail(e);

    fetch(`${API}/api/community/profile/${encodeURIComponent(e)}`)
      .then(r => r.json())
      .then(d => {
        if (d?.success) {
          const c = (d.profile?.country || '').trim();
          if (!c) setShow(true);
        }
      })
      .catch(() => { /* silent — non-blocking */ });
  }, []);

  const skip = () => {
    // Skip for 24h
    localStorage.setItem(SKIP_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setShow(false);
  };

  const save = async () => {
    const c = country.trim();
    if (c.length < 2) { setErr(t('countryModal.invalid')); return; }
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/community/profile/${encodeURIComponent(email)}/country`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: c }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setShow(false);
    } catch (e) {
      setErr(t('countryModal.error'));
    }
    setSaving(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="country-backfill-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            <div>
              <h3 className="text-base font-semibold">{t('countryModal.title')}</h3>
              <p className="text-[11px] text-white/80">{t('countryModal.subtitle')}</p>
            </div>
          </div>
          <button onClick={skip} className="hover:opacity-80" aria-label={t('countryModal.skip')} data-testid="country-backfill-skip">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-700">{t('countryModal.description')}</p>
          <input
            type="text"
            value={country}
            onChange={e => { setCountry(e.target.value); setErr(null); }}
            onKeyDown={e => e.key === 'Enter' && save()}
            placeholder={t('countryModal.placeholder')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 outline-none"
            autoFocus
            data-testid="country-backfill-input"
          />
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button
              onClick={skip}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              data-testid="country-backfill-skip-btn"
            >
              {t('countryModal.skip')}
            </button>
            <button
              onClick={save}
              disabled={saving || country.trim().length < 2}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-cyan-600 text-white text-sm rounded-lg disabled:opacity-50"
              data-testid="country-backfill-save"
            >
              {saving ? t('countryModal.saving') : t('countryModal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
